import * as Sentry from '@sentry/browser';
import config from '../config';
import { accessTokenLocalStoragePath } from '../constants';
import { ErrorType, RedirectAttrs } from '../models/commonModels';

const unauthorizedCause = 'TIMED_OUT_SESSION?';

const parseErrorCode = (response?: Response, error?: ErrorType): string =>
    (error && error.error) || (response && response.status === 401 ? unauthorizedCause : 'unknown error');

const writeToSentry = (error: ErrorType, response: Response, url?: string | URL): void => {
    if (Sentry) {
        const errorCode = parseErrorCode(response, error);
        Sentry.captureEvent({
            message: `fetch received error response - ${errorCode}`,
            extra: { response, error, error_description: error.error_description },
            level: 'warning',
            tags: {
                ...error,
                statusCode: String(response.status),
                url: url ? url.toString() : 'unknown',
            },
        });
    }
};

const statusHelper = <T>(response: Response, url?: string | URL): Promise<T> => {
    if (response.status === 204) {
        return Promise.resolve({} as Promise<T>);
    }
    if (response.status >= 200 && response.status < 300) {
        return Promise.resolve(response.json() as Promise<T>);
    }

    return response.json().then(json => {
        writeToSentry(json, response, url);
        return Promise.reject(json);
    });
};

export const getAuthorizationHeaders = (): HeadersInit => {
    return { Authorization: `Bearer ${localStorage.getItem(accessTokenLocalStoragePath)}` };
};

const configureUrl = (url: string, client?: RedirectAttrs): URL => {
    const urlString = `${config.apiUrl}${url}`;

    const typedUrl = new URL(urlString);
    const searchParams = typedUrl.searchParams;
    if (client && client.client_id) {
        searchParams.append('client_id', client.client_id);
    }
    if (client && client.redirect_uri) {
        searchParams.append('redirect_uri', client.redirect_uri);
    }
    return typedUrl;
};

export const refreshAccessToken = <T>(refreshToken: string, client: RedirectAttrs): Promise<T> =>
    fetchFromAccounts(
        '/authenticate',
        {
            method: 'POST',
            body: JSON.stringify({
                refresh_token: refreshToken,
                grant_type: 'refresh_token',
            }),
        },
        { ...client, isRefreshing: 'true' }
    );

const authenticationRefreshHelper = (
    response: Response,
    request: { url: string; options: RequestInit; client?: RedirectAttrs }
): Promise<Response> => {
    const { client } = request;
    if (client && !client.isRefreshing && response.status === 401) {
        const refreshToken = localStorage.getItem('airthingsAccounts.refreshToken');
        if (refreshToken && refreshToken !== 'undefined') {
            refreshAccessToken(refreshToken, client);
            return fetchFromAccounts(request.url, request.options, { ...client, isRefreshing: 'true' });
        }
        return Promise.reject(response);
    }
    return Promise.resolve(response);
};

export const fetchFromAccounts = <T>(url: string, options: RequestInit, client?: RedirectAttrs): Promise<T> => {
    return fetch(configureUrl(url, client), {
        ...options,
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...options.headers,
        },
    })
        .then(res => authenticationRefreshHelper(res, { url, options, client }))
        .then(res => statusHelper(res, url));
};

export default fetchFromAccounts;
